/** SUNTRONIX ONE — Boldable description editor.
 *  A textarea with a single Bold control that wraps the SELECTED text in
 *  **markdown** (never the whole field). Stored value stays a plain string
 *  with ** markers — safe for DB / save / revision / Vtiger. The quotation
 *  PDF renderer interprets ** as selective bold; ** never appears in output.
 *  Includes a live preview so the user sees exactly what will be emphasised. */
import { useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Bold, Eye, Sparkles, Undo2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const escapeHtml = (s) =>
  String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function renderBoldPreview(text) {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");
}

export function BoldableTextarea({
  value, onChange, rows = 2, placeholder, testid, className = "", disabled = false,
  enableSmartFormat = false, smartContext = null,
}) {
  const ref = useRef(null);
  const [showPrev, setShowPrev] = useState(true);
  const [smartBusy, setSmartBusy] = useState(false);
  const [undoValue, setUndoValue] = useState(null); // snapshot before smart format
  const v = value || "";

  const smartFormat = async () => {
    if (disabled || smartBusy) return;
    setSmartBusy(true);
    try {
      const { data } = await api.post("/descriptions/smart-format",
        { text: v, brand: smartContext?.brand || undefined, context: smartContext || undefined });
      if (!data.changed) {
        toast.info("Nothing to emphasise — description left unchanged");
      } else {
        setUndoValue(v);
        onChange(data.formatted);
        const pct = Math.round((data.bold_ratio || 0) * 100);
        toast.success(`Smart Format applied · ${pct}% emphasised`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Smart Format failed");
    }
    setSmartBusy(false);
  };

  const undoSmart = () => {
    if (undoValue == null) return;
    onChange(undoValue);
    setUndoValue(null);
    toast.success("Smart Format undone");
  };

  const setAndSelect = (nv, selStart, selEnd) => {
    onChange(nv);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el) { el.focus(); el.setSelectionRange(selStart, selEnd); }
    });
  };

  const toggleBold = () => {
    const el = ref.current;
    if (!el || disabled) return;
    const start = el.selectionStart ?? v.length;
    const end = el.selectionEnd ?? v.length;
    if (start === end) {
      setAndSelect(v.slice(0, start) + "****" + v.slice(end), start + 2, start + 2);
      return;
    }
    const sel = v.slice(start, end);
    const before2 = v.slice(Math.max(0, start - 2), start);
    const after2 = v.slice(end, end + 2);
    if (sel.startsWith("**") && sel.endsWith("**") && sel.length >= 4) {
      const inner = sel.slice(2, -2);
      setAndSelect(v.slice(0, start) + inner + v.slice(end), start, start + inner.length);
    } else if (before2 === "**" && after2 === "**") {
      setAndSelect(v.slice(0, start - 2) + sel + v.slice(end + 2), start - 2, end - 2);
    } else {
      setAndSelect(v.slice(0, start) + "**" + sel + "**" + v.slice(end), start + 2, end + 2);
    }
  };

  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
      e.preventDefault();
      toggleBold();
    }
  };

  return (
    <div data-testid={testid ? `${testid}-wrap` : undefined}>
      <div className="flex items-center gap-1.5 mb-1">
        <button type="button" disabled={disabled}
          onMouseDown={(e) => e.preventDefault()} onClick={toggleBold}
          data-testid={testid ? `${testid}-bold` : undefined}
          title="Bold the selected text (Ctrl/Cmd + B)"
          className="h-6 px-2 rounded border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-40 text-[11px] font-bold inline-flex items-center gap-1 transition-colors">
          <Bold className="h-3 w-3" /> Bold
        </button>
        <span className="text-[10px] text-slate-400">Select important specs, then Bold — keep the rest regular.</span>
        {enableSmartFormat && (
          <button type="button" disabled={disabled || smartBusy}
            onMouseDown={(e) => e.preventDefault()} onClick={smartFormat}
            data-testid={testid ? `${testid}-smart` : undefined}
            title="Auto-emphasise key specs (SKU, processor, RAM, storage…)"
            className="h-6 px-2 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40 text-[11px] font-medium inline-flex items-center gap-1 transition-colors">
            {smartBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Smart Format
          </button>
        )}
        {enableSmartFormat && undoValue != null && (
          <button type="button" onClick={undoSmart}
            data-testid={testid ? `${testid}-undo-smart` : undefined}
            title="Undo Smart Format"
            className="h-6 px-2 rounded border border-slate-200 text-slate-600 hover:bg-slate-100 text-[11px] inline-flex items-center gap-1 transition-colors">
            <Undo2 className="h-3 w-3" /> Undo
          </button>
        )}
        <button type="button" onClick={() => setShowPrev((s) => !s)}
          className="ml-auto text-[10px] text-slate-400 hover:text-slate-600 inline-flex items-center gap-1">
          <Eye className="h-3 w-3" /> {showPrev ? "Hide" : "Show"} preview
        </button>
      </div>
      <Textarea ref={ref} rows={rows} value={v} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} onKeyDown={onKey}
        placeholder={placeholder} className={className} data-testid={testid} />
      {showPrev && v.trim() && (
        <div className="mt-1">
          <span className="text-[9px] uppercase tracking-wider text-slate-400">Preview (how it prints)</span>
          <div className="mt-0.5 rounded border border-slate-100 bg-slate-50 px-2 py-1 text-[11px] text-slate-700 leading-relaxed break-words"
            data-testid={testid ? `${testid}-preview` : undefined}
            dangerouslySetInnerHTML={{ __html: renderBoldPreview(v) }} />
        </div>
      )}
    </div>
  );
}

export default BoldableTextarea;
